import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./qualifiedChat.html";
import { QUALIFIED_SRC_URL } from '~/modules/config';
import { QUALIFIED_CHAT_LOAD_BUDGET_MS } from '~/modules/qualified_chat_constants';
import { handleException } from '~/modules/utilities/error_utilities';
import { logger } from '~/modules/utilities/logger';

// eslint-disable-next-line no-var
// @ts-expect-error * Automated Type Error Fix

const QUALIFIED_COOKIE_GROUP = '3';
const QUALIFIED_CHAT_REGEX = new RegExp(QUALIFIED_COOKIE_GROUP);
const QUALIFIED_CHAT_ID = 'qualified-chat-loader';
// QUALIFIED_CHAT_LOAD_BUDGET_MS lives in ~/modules/qualified_chat_constants so the qualified_chat
// e2e suite can share it .
const QUALIFIED_CHAT_MAX_RETRIES = 3;
const QUALIFIED_CHAT_RETRY_DELAY_MS = 1000;
// Cap the exponential backoff so raising QUALIFIED_CHAT_MAX_RETRIES can't blow the delay up
// (e.g. at 10 retries the uncapped final delay would be 1000 * 2^9 ≈ 512s).
const QUALIFIED_CHAT_MAX_RETRY_DELAY_MS = 10000;

// Null the load handlers before removing an abandoned <script> so a late-landing fetch can't fire
// onload/onerror after we've moved on (a "zombie" handler). Mirrors tbid.ts's loadSFID cleanup.
function detachScript(script) {
  if (script) {
    script.onload = null;
    script.onerror = null;
    script.remove();
  }
}
function loadQualifiedChat() {
  // If we've already loaded the widget, do nothing
  // eslint-disable-next-line @lwc/lwc/no-document-query
  if (document.getElementById(QUALIFIED_CHAT_ID)) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    let settled = false;
    let currentScript = null;
    const timers = [];

    // Single settle path: guards against double-settle and clears every outstanding timer (the
    // overall budget plus any pending retry backoff) so nothing dangles after the load succeeds
    // or gives up.
    const settle = finish => {
      if (settled) {
        return;
      }
      settled = true;
      timers.forEach(clearTimeout);
      finish();
    };
    const attempt = retriesLeft => {
      if (settled) {
        return;
      }
      const script = document.createElement('script');
      script.id = QUALIFIED_CHAT_ID;
      script.async = false;
      script.src = QUALIFIED_SRC_URL;
      script.onload = () => settle(resolve);
      script.onerror = () => {
        // Definitive failure: drop this element (detaching handlers) and retry with backoff
        // while the budget allows. A timeout, by contrast, never lands here — it doesn't retry.
        detachScript(script);
        if (settled) {
          return;
        }
        if (retriesLeft > 0) {
          // Anticipated degradation on an interrupted CDN load; feature degrades gracefully.
          // See docs/logging-decision-guide.md ("External script fails to load (CDN)" -> warn).
          logger.warn(`[qualifiedChat] Chat widget failed to load. Retrying... (${retriesLeft} attempts left)`);
          const backoff = Math.min(QUALIFIED_CHAT_RETRY_DELAY_MS * Math.pow(2, QUALIFIED_CHAT_MAX_RETRIES - retriesLeft), QUALIFIED_CHAT_MAX_RETRY_DELAY_MS);
          timers.push(setTimeout(() => attempt(retriesLeft - 1), backoff));
        } else {
          // Retries exhausted: let connectedCallback's catch log at warn (benign, per W-22717334).
          settle(() => reject(new Error('QUALIFIED_CHAT_LOAD_ERROR')));
        }
      };
      currentScript = script;
      document.head.appendChild(script);
    };

    // One budget spanning all attempts. On expiry we give up on the in-flight load (detaching its
    // handlers so a late onload can't resolve after we've stopped waiting) and reject — we do NOT
    // retry, because the fetch may simply need more time and refetching wouldn't help.
    timers.push(setTimeout(() => {
      detachScript(currentScript);
      settle(() => reject(new Error('QUALIFIED_CHAT_LOAD_TIMEOUT')));
    }, QUALIFIED_CHAT_LOAD_BUDGET_MS));
    attempt(QUALIFIED_CHAT_MAX_RETRIES);
  });
}
class QualifiedChat extends LightningElement {
  constructor(...args) {
    super(...args);
    this.usertoken = void 0;
    // Guards the setter's re-init path so it only fires once the initial mount has loaded the
    // script and initialized the widget. Set true at the end of connectedCallback.
    this._initialized = false;
  }
  get chattoken() {
    return this._chattoken;
  }
  set chattoken(value) {
    const changed = value !== this._chattoken;
    this._chattoken = value;
    // On a SPA listing->listing navigation the same <ace-qualified-chat> element is reused
    // (displayChat stays true across two chat-enabled listings), so connectedCallback never
    // fires again to swap the widget. Re-initialize here when the token changes on an
    // already-initialized widget; the first mount is owned by connectedCallback.
    if (this._initialized && changed && value) {
      qualified('teardown');
      this.initializeChat();
    }
  }
  async connectedCallback() {
    try {
      await loadQualifiedChat();
      this.bootstrapQualifiedGlobal();
      this.initializeChat();
      this._initialized = true;
    } catch (exception) {
      handleException(exception, 'qualifiedChat.connectedCallback', {
        level: 'warn'
      });
    }
  }
  disconnectedCallback() {
    qualified('teardown');
  }
  bootstrapQualifiedGlobal() {
    // May the lord have mercy on our souls
    (function (w, q) {
      // eslint-disable-next-line dot-notation
      // @ts-expect-error * Automated Type Error Fix
      w.QualifiedObject = q;
      // @ts-expect-error * Automated Type Error Fix
      w[q] =
      // @ts-expect-error * Automated Type Error Fix
      w[q] ||
      // @ts-expect-error * Automated Type Error Fix
      function (args) {
        // @ts-expect-error * Automated Type Error Fix
        (w[q].q = w[q].q || []).push(args);
      };
    })(window, 'qualified');
  }
  initializeChat() {
    qualified('initialize', this._chattoken, {
      hasCookieConsent: this.isQualifiedCookieAvailable(),
      appexchangeId: this.usertoken,
      appexchange: true
    });
  }
  isQualifiedCookieAvailable() {
    return window.OptanonActiveGroups?.match(QUALIFIED_CHAT_REGEX) != null;
  }
  sendAnalyticsChatInitEvent() {
    const chatInitEvent = new CustomEvent('analytics', {
      bubbles: true,
      composed: true,
      detail: {
        eventType: 'chatInit'
      }
    });
    this.dispatchEvent(chatInitEvent);
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(QualifiedChat, {
  publicProps: {
    usertoken: {
      config: 0
    },
    chattoken: {
      config: 3
    }
  },
  fields: ["_initialized"]
});
const __lwc_component_class_internal = _registerComponent(QualifiedChat, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;